import { useState, useCallback, useMemo } from 'react'
import { UIEntryFieldItem } from '../store/schemas'
import clsx from 'clsx'
import ViewSourceEditPopover from '../../references/components/ViewSourceEditPopover'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMedSummaryReferenceById } from '@/medical-billing-summaries/references/store/selectors'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'

interface ReferencedItemProps {
  item: UIEntryFieldItem
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
  highlightsEnabled?: boolean
  isCellHovered?: boolean
  isCurrency?: boolean
  onStartEdit: () => void
}

/**
 * Referenced Item
 *
 * Displays a single referenced item field for billing summary data.
 * Wrapped with ViewSourceEditPopover to enable the popover on hover.
 */
export default function ReferencedItem(props: ReferencedItemProps) {
  const { item, cellPosition, scrollToCell, highlightsEnabled = true, isCellHovered = false, isCurrency = false, onStartEdit } = props
  const { id: parentId } = useMedSummaryParent()
  const [isHighlighted, setIsHighlighted] = useState(false)

  // Get the reference from the store if referenceId exists
  const storeReference = useAppSelector((state: RootState) =>
    item.referenceId ? selectMedSummaryReferenceById(state, { parentId, referenceId: item.referenceId }) : null
  )

  // Strip highlights for fields without reliable bboxes (e.g. non-amount fields).
  // View Source still opens the PDF to the correct page, just without bbox overlays.
  const reference = useMemo(
    () => (storeReference && !highlightsEnabled ? { ...storeReference, highlights: [] } : storeReference),
    [storeReference, highlightsEnabled]
  )
  // Check if the reference is currently open in the content viewer
  const isActive = useAppSelector((state: RootState) =>
    item.referenceId
      ? selectActiveContentViewerContextMatches(state, { kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE, referenceId: item.referenceId })
      : false
  )

  // Check if any field is being edited
  const isAnyFieldEditing = useAppSelector((state) => state.medSummaryEntryEditState.activeEdit !== null)

  // Memoize the highlight change callback to prevent unnecessary re-renders in ViewSourceEditPopover
  const handleItemHighlightChange = useCallback((isHighlighted: boolean) => {
    setIsHighlighted(isHighlighted)
  }, [])

  // Inferred values
  const hasReference = item.referenceId !== null && reference !== null
  const hasValue = item.text !== null && item.text.trim().length > 0

  // Display blank for all null/empty values
  const displayText = item.text ?? ''
  const showBlank = !hasValue

  // For currency, the text is already formatted as "1,234.56" from the selector
  // We just need to add the dollar sign when rendering
  const currencyParts = isCurrency ? { dollarSign: '$', value: displayText } : null

  // Base item styles with interaction and visibility states for both currency and text items
  const baseItemStyles = clsx(
    'text-sm rounded-sm px-1',
    showBlank ? 'pointer-events-none opacity-0' : 'pointer-events-auto',
    isHighlighted && hasReference && 'bg-brand-neutral-100',
    isActive && 'bg-yellow-100',
    isActive || isAnyFieldEditing || showBlank || !hasReference ? 'cursor-default' : 'cursor-pointer'
  )

  return (
    <ViewSourceEditPopover
      reference={reference}
      cellPosition={cellPosition}
      scrollToCell={scrollToCell}
      isCellHovered={isCellHovered}
      onItemHighlightChange={handleItemHighlightChange}
      onStartEdit={onStartEdit}
      // The amount cell uses pr-3, so apply an additive delta to keep the popover aligned with other columns
      alignOffsetDelta={isCurrency ? -12 : undefined}
    >
      <div className="relative group">
        {isCurrency && currencyParts ? (
          // Currency item
          <div className={clsx(baseItemStyles, 'flex justify-between gap-1 w-fit min-w-full')}>
            <span>{currencyParts.dollarSign}</span>
            <span>{currencyParts.value}</span>
          </div>
        ) : (
          // Regular text item
          <span className={clsx(baseItemStyles, 'py-0.5 inline whitespace-pre')}>{displayText}</span>
        )}
      </div>
    </ViewSourceEditPopover>
  )
}
